// src/pages/MagicLoginPage.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const MagicLoginPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();

  const authenticateWithMagicLink = useCallback(async (profileId: string, confirmationId: string, token?: string) => {
    try {
      setLoading(true);
      const response = await api.post('/auth/magic-link', { profileId, confirmationId, token });

      const { accessToken, refreshToken } = response.data;

      localStorage.setItem('token', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

      setTimeout(() => {
        navigate('/dashboard');
        window.location.reload();
      }, 1000);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
        t('magicLogin.failedToAuthenticate')
      );
      setLoading(false);
    }
  }, [navigate, t]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
      return;
    }

    const profileId = searchParams.get('profileId');
    const confirmationId = searchParams.get('confirmationId');
    const token = searchParams.get('token');

    if (!profileId || !confirmationId) {
      setError(t('magicLogin.invalidLink'));
      setLoading(false);
      return;
    }

    authenticateWithMagicLink(profileId, confirmationId, token || undefined);
  }, [searchParams, isAuthenticated, navigate, t, authenticateWithMagicLink]);

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={60} sx={{ mb: 3 }} />
              <Typography variant="h5" component="h1" gutterBottom>
                {t('magicLogin.authenticating')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('magicLogin.pleaseWait')}
              </Typography>
            </>
          ) : error ? (
            <>
              <Alert severity="error" sx={{ width: '100%', mb: 3 }}>
                {error}
              </Alert>
              <Typography variant="body2" color="text.secondary" align="center">
                {t('magicLogin.linkExpired')}
              </Typography>
            </>
          ) : (
            <>
              <Alert severity="success" sx={{ width: '100%', mb: 3 }}>
                {t('magicLogin.loginSuccessful')}
              </Alert>
              <Typography variant="body2" color="text.secondary">
                {t('magicLogin.redirecting')}
              </Typography>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default MagicLoginPage;

